import React, { useState } from "react";
import TuneIcon from "@material-ui/icons/Tune";
import {
  Box,
  FormControlLabel,
  IconButton,
  Popover,
  Switch,
  Typography,
} from "@material-ui/core";
import {
  usePopupState,
  bindTrigger,
  bindPopover,
} from "material-ui-popup-state/hooks";
import { ObjectPermissionSetData } from "types/permissions";
import { postCustomCTA, setBuilderPermissions } from "adapters";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  GetUrlBuilderData,
  SetUrlBuilderData,
  UpdateBuilderPermissions,
} from "store/url-builder";
import { ChangeConfDialog } from "../changable-confirm-dialog";
import { toast } from "react-toastify";
import { updateCard, updateCardsByTemplate } from "adapters/public-card";
import { GetCardData } from "store/card";

export interface ModuleSettingsProps {
  permissions: ObjectPermissionSetData[];
  builderType: string;
}

export const ModuleSettings = ({
  permissions = [],
  builderType,
}: ModuleSettingsProps) => {
  const { cardId }: any = useParams();
  const dispatch = useDispatch();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "ModuleSettings",
  });

  const builderData = useSelector(GetUrlBuilderData);
  const portCard = useSelector(GetCardData);

  const [confirmDialogue, setConfirmDialogue] = useState(false);
  const [permission, setPermission] = useState<ObjectPermissionSetData | null>(
    null
  );

  const [loading, setLoading] = useState(false);

  const newPermissions = permissions;

  const updatePermission = async (permission: ObjectPermissionSetData) => {
    setLoading(true);
    const updatedPermissionData = await setBuilderPermissions(
      cardId,
      permission
    );
    if (!updatedPermissionData.data.success)
      throw new Error(updatedPermissionData.data.message);

    await enablePublish();

    setConfirmDialogue(false);
    setPermission(null);
    setLoading(false);
    dispatch(UpdateBuilderPermissions(updatedPermissionData.data.data));
  };

  //a custom api is calling to get publish buttom enabled from response
  const enablePublish = async () => {
    await postCustomCTA(cardId, builderType, builderData.customCTA)
      .then(({ data }) => {
        if (!data.success) return;
        dispatch(SetUrlBuilderData(data.data));
      })
      .catch((err) => {
        toast.error("Error while enabling publish");
      });
  };

  return (
    <>
      <IconButton {...bindTrigger(popupState)}>
        <TuneIcon />
      </IconButton>
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box padding=".5em">
          <Typography variant="body2" gutterBottom>
            <b>Permissions</b>
          </Typography>
          {newPermissions.map((permission, idx) => {
            if (
              !(
                builderType === "PRO" &&
                permission.objectPermission.objectPermissionName ===
                  "changeable"
              )
            ) {
              return (
                <Box key={idx}>
                  <FormControlLabel
                    control={
                      <Switch
                        disabled={
                          !newPermissions.find(
                            (p) =>
                              p.objectPermission.objectPermissionName ===
                              "changeable"
                          )?.objectPermissionValue && builderType === "PRO"
                        }
                        checked={permission.objectPermissionValue}
                        onChange={() => {
                          if (
                            builderType !== "PRO" &&
                            permission.objectPermission.objectPermissionName ===
                              "changeable"
                          ) {
                            setPermission({
                              ...permission,
                              objectPermissionValue:
                                !permission.objectPermissionValue,
                            });
                            setConfirmDialogue(true);
                          } else {
                            updatePermission({
                              ...permission,
                              objectPermissionValue:
                                !permission.objectPermissionValue,
                            });
                          }
                        }}
                        color="primary"
                      />
                    }
                    label={permission.objectPermission.objectPermissionName}
                  />
                </Box>
              );
            }
          })}
        </Box>
      </Popover>
      {confirmDialogue && permission && (
        <ChangeConfDialog
          open={confirmDialogue}
          onClose={() => {
            setConfirmDialogue(false);
            setPermission(null);
          }}
          permission={permission}
          onSubmit={() => {
            updatePermission(permission);
          }}
          loading={loading}
          error
        />
      )}
    </>
  );
};
